'use client';

import type { ImageProps } from 'next/image';
import type { ComponentType } from 'react';
import type { IllustrationSlotName } from '@/components/features/campaigns';
import type { Illustration } from '@/libs/campaigns/types';
import Image from 'next/image';
import { useId } from 'react';
import { useIllustration } from '@/components/features/campaigns';
import { cn } from '@/utils/cn';

interface Props {
  slot: IllustrationSlotName;
  alt: string;
  className?: string;
  imageProps?: Omit<ImageProps, 'src' | 'alt' | 'className'>;
  ImageComponent?: ComponentType<ImageProps>;
}

const percent = (value: number, of: number) => `${(value / of) * 100}%`;

type LayeredIllustrationProps = {
  illustration: Extract<Illustration, { type: 'layers' }>;
  alt: string;
  className?: string;
};

const LayeredIllustration = ({ illustration, alt, className }: LayeredIllustrationProps) => {
  const id = useId();
  const { width, height, layers } = illustration;

  return (
    <div
      {...(alt ? { 'role': 'img', 'aria-label': alt } : { 'aria-hidden': true })}
      className={cn('relative', className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {layers.map((layer, index) => {
        if (layer.kind === 'glow') {
          const gradientId = `${id}-glow-gradient-${index}`;
          const filterId = `${id}-glow-blur-${index}`;

          return (
            <svg
              key={layer.path}
              aria-hidden
              className="pointer-events-none absolute inset-0 size-full overflow-visible"
              viewBox={`0 0 ${width} ${height}`}
            >
              <defs>
                <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1={layer.gradient.x1} y1={layer.gradient.y1} x2={layer.gradient.x2} y2={layer.gradient.y2}>
                  <stop stopColor={layer.gradient.from} />
                  <stop offset="1" stopColor={layer.gradient.to} />
                </linearGradient>
                <filter id={filterId} filterUnits="userSpaceOnUse" x={-width * 4} y={-height * 4} width={width * 9} height={height * 9} colorInterpolationFilters="sRGB">
                  <feGaussianBlur stdDeviation={layer.blur} />
                </filter>
              </defs>
              <g filter={`url(#${filterId})`}>
                <path d={layer.path} transform={layer.transform} fill={`url(#${gradientId})`} />
              </g>
            </svg>
          );
        }

        const placement = {
          left: percent(layer.x, width),
          top: percent(layer.y, height),
          width: percent(layer.width, width),
          height: percent(layer.height, height),
        };

        if (layer.kind === 'video') {
          return (
            <video
              key={layer.video.webm}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={layer.video.poster}
              aria-hidden
              className="pointer-events-none absolute max-w-none select-none"
              style={placement}
            >
              <source src={layer.video.mov} type="video/quicktime" />
              <source src={layer.video.webm} type="video/webm" />
            </video>
          );
        }

        return (
          <Image
            key={`${layer.x}:${layer.y}:${layer.width}:${layer.height}`}
            src={layer.image}
            alt=""
            className="pointer-events-none absolute max-w-none select-none"
            style={{ ...placement, mixBlendMode: layer.blendMode }}
          />
        );
      })}
    </div>
  );
};

export const IllustrationSlot = ({ slot, alt, className, imageProps, ImageComponent = Image }: Props) => {
  const illustration = useIllustration(slot);

  if (!illustration) {
    return null;
  }

  if (illustration.type === 'video') {
    return (
      <video
        key={illustration.video.webm}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster={illustration.video.poster}
        aria-label={alt}
        className={className}
      >
        <source src={illustration.video.mov} type="video/quicktime" />
        <source src={illustration.video.webm} type="video/webm" />
      </video>
    );
  }

  if (illustration.type === 'layers') {
    return <LayeredIllustration illustration={illustration} alt={alt} className={className} />;
  }

  return <ImageComponent {...imageProps} src={illustration.image} alt={alt} className={className} />;
};
